import { ChangeEvent, useState } from 'react';
import { Button, Divider, Input, List, message, Modal, Space, Spin, Table } from 'antd';
import type { ColumnsType, FilterValue } from 'antd/es/table/interface';
import { debounce } from 'lodash';

import { Role } from '@app/domain-interfaces';
import { WorkerDto } from '@app/integ-apigee-interfaces';
import { DmsTicketRoo } from '@app/integ-hsdes-interfaces';
import { Auth } from '@app/web-components';

import { useFindEvidenceByEnterpriseId } from '../data';

const DEFAULT_PAGE_SIZE = 10;

export type EvidenceModalProps = {
  enterpriseId: string | undefined;
};

export const EvidenceModal = ({ enterpriseId }: EvidenceModalProps) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [show, setShow] = useState(false);
  const { data, isFetching } = useFindEvidenceByEnterpriseId(enterpriseId || '', show);
  const { user } = Auth.useUserAuth();

  const handleClick = () => {
    if (!enterpriseId) {
      messageApi.open({
        type: 'error',
        content: 'Select a worker first',
      });
      return;
    }
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="User Evidence"
        width="80%"
        styles={{
          body: {
            height: 600,
            overflow: 'auto',
            paddingRight: 10,
          },
        }}
        open={show}
        onCancel={handleClose}
        footer={[
          <Button key="ok" onClick={handleClose} type="primary">
            Ok
          </Button>,
        ]}
      >
        {isFetching ? (
          <Spin tip="Gathering data..." style={{ width: '100%' }}>
            <div style={{ height: 50 }} />
          </Spin>
        ) : data ? (
          <Space direction="vertical" size="small">
            <Divider orientation="left">HR Worker Information</Divider>
            <WorkerTable worker={data.worker} />
            <Divider orientation="left">DMS Tickets</Divider>
            <DmsTicketList tickets={data.dmsTickets} />
          </Space>
        ) : undefined}
      </Modal>
      {!enterpriseId || !user?.roles[Role.SYSTEM_ADMIN] ? undefined : (
        <Button size="small" onClick={handleClick}>
          View Evidence
        </Button>
      )}
    </>
  );
};

type WorkerTableProps = {
  worker: WorkerDto;
};

const WorkerTable = ({ worker }: WorkerTableProps) => {
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});

  const data = Object.entries(worker).map(([field, value]) => ({ field, value: value ? value.toString() : '' }));

  const columns: ColumnsType<{ field: string; value: string }> = [
    {
      title: 'Field',
      dataIndex: 'field',
      ellipsis: true,
      filteredValue: filteredInfo.field || null,
      onFilter: (value, record) => record.field.toLowerCase().includes((value || '').toString().toLowerCase()),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      ellipsis: true,
    },
  ];

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilteredInfo((prev) => ({
      ...prev,
      field: e.target.value ? [e.target.value] : null,
    }));
  };

  return (
    <>
      <Space style={{ marginBottom: 20 }}>
        <Input style={{ width: 350 }} placeholder="Search by field..." onChange={debounce(handleSearchChange, 300)} />
      </Space>
      <Table
        rowKey="field"
        columns={columns}
        dataSource={data}
        size="small"
        // scroll={{ y: 300 }}
        pagination={{ pageSize: DEFAULT_PAGE_SIZE }}
      />
    </>
  );
};

type DmsTicketListProps = {
  tickets: DmsTicketRoo[] | null;
};

const DmsTicketList = ({ tickets }: DmsTicketListProps) => {
  return (
    <List
      itemLayout="horizontal"
      style={{
        maxWidth: '100%',
        overflow: 'auto',
      }}
      size="small"
      dataSource={tickets || []}
      renderItem={(item) => (
        <List.Item style={{ paddingTop: 0, paddingRight: 10, paddingBottom: 0, paddingLeft: 10 }}>
          <List.Item.Meta title={item.title} description={<pre>{JSON.stringify(item, null, 2)}</pre>} />
        </List.Item>
      )}
    />
  );
};
